import { useEffect, useState } from "react";
import { Wallet, UserCheck, ShieldCheck, Coins, PlusCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtMYT } from "@/lib/myt";

export default function AdminLocalDepositors() {
  const [active, setActive] = useState(undefined); // undefined = loading, null = none
  const [ibs, setIbs] = useState([]);
  const [selectedIb, setSelectedIb] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [sendAmount, setSendAmount] = useState("");
  const [sendError, setSendError] = useState("");
  const [confirm, setConfirm] = useState(null); // {amount, token} when modal open
  const [allocations, setAllocations] = useState([]);

  const load = () => {
    api.get("/admin/local-depositors/active").then((res) => setActive(res.data.active)).catch(() => setActive(null));
    api.get("/admin/local-depositors/ibs").then((res) => setIbs(res.data)).catch(() => setIbs([]));
    api.get("/admin/local-depositors/allocations").then((res) => setAllocations(res.data.allocations || [])).catch(() => setAllocations([]));
  };

  useEffect(load, []);

  const assign = async () => {
    if (!selectedIb) { setError("Select an IB to assign."); return; }
    setBusy(true);
    setError("");
    try {
      await api.post("/admin/local-depositors/assign", { ib_user_id: selectedIb });
      toast.success("Local Depositor assigned");
      setSelectedIb("");
      load();
    } catch (err) {
      setError(formatApiError(err));
    }
    setBusy(false);
  };

  const balance = active?.balance_coins ?? 0;
  const amountNum = Number(sendAmount);
  const amountValid = sendAmount !== "" && Number.isInteger(amountNum) && amountNum > 0;
  const balanceAfter = amountValid ? balance + amountNum : null;

  const openConfirm = () => {
    setSendError("");
    if (!amountValid) { setSendError("Enter a positive whole number of COINS."); return; }
    // Generate the idempotency token ONCE when opening the confirm — reused on retry.
    setConfirm({ amount: amountNum, token: (window.crypto?.randomUUID?.() || String(Date.now())) });
  };

  const submitSend = async () => {
    setBusy(true);
    try {
      const res = await api.post("/admin/local-depositors/allocate", { amount: confirm.amount, client_token: confirm.token });
      toast.success(res.data.duplicated ? "Allocation already processed" : `Sent ${confirm.amount} COINS — new balance ${res.data.balance_coins}`);
      setConfirm(null);
      setSendAmount("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy(false);
  };

  const labelCls = "block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide";

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="admin-local-depositors-page">
      <div>
        <p className="eyebrow-label">Administration</p>
        <h1 className="page-title mt-2">Local Depositor</h1>
        <p className="page-sub mt-1">Assign one IB as the active Local Depositor and top up their COINS inventory. Settlement between Admin and Depositor happens outside PLUSMAKER — only COINS inventory is tracked here.</p>
      </div>

      {/* Active depositor */}
      <div className="card-lux p-6" data-testid="active-local-depositor">
        <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B] flex items-center gap-2">
          <UserCheck className="h-3.5 w-3.5 text-[#D4AF37]" /> Active Local Depositor
        </p>
        {active === undefined ? (
          <p className="mt-4 text-sm text-[#64748B]" data-testid="ld-loading">Loading…</p>
        ) : active === null ? (
          <p className="mt-4 text-sm text-[#94A3B8]" data-testid="ld-none">No active Local Depositor.</p>
        ) : (
          <div className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-3" data-testid="ld-active-details">
            {[
              ["Username", active.ib?.username],
              ["Full Name", active.ib?.full_name],
              ["UID", active.ib?.uid],
              ["Email", active.ib?.email],
              ["Status", active.status],
              ["Assigned", active.assigned_at ? `${fmtMYT(active.assigned_at)}${active.assigned_by_name ? ` · by ${active.assigned_by_name}` : ""}` : "—"],
            ].map(([label, val]) => (
              <div key={label}>
                <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B]">{label}</p>
                <p className="text-sm text-white mt-0.5 font-mono break-all">{val || "—"}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Assign existing IB — only when none active */}
      {active === null && (
        <div className="card-lux p-6 space-y-4" data-testid="assign-local-depositor">
          <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B]">Assign an existing IB</p>
          <div>
            <label className={labelCls}>Select IB</label>
            <select value={selectedIb} onChange={(e) => setSelectedIb(e.target.value)} className="input-lux" data-testid="assign-ld-select">
              <option value="">— Choose an IB —</option>
              {ibs.map((ib) => (
                <option key={ib.id} value={ib.id}>{ib.full_name} · {ib.username} ({ib.uid})</option>
              ))}
            </select>
          </div>
          {error && <p className="text-sm text-red-400 bg-red-400/10 border border-red-400/25 rounded-lg px-4 py-3" data-testid="assign-ld-error">{error}</p>}
          <button onClick={assign} disabled={busy || !selectedIb} className="btn-gold px-6 py-2.5 text-sm disabled:opacity-50 flex items-center gap-2" data-testid="assign-ld-button">
            <Wallet className="h-4 w-4" /> {busy ? "Assigning…" : "Assign as Local Depositor"}
          </button>
        </div>
      )}

      {active && (
        <>
          {/* Inventory + Send COINS */}
          <div className="card-lux p-6 space-y-5" data-testid="ld-inventory">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B] flex items-center gap-2">
                <Coins className="h-3.5 w-3.5 text-[#D4AF37]" /> Local Depositor Inventory
              </p>
              <p className="text-3xl font-bold text-[#D4AF37] font-mono" data-testid="ld-inventory-balance">{balance.toLocaleString()} <span className="text-sm text-[#64748B]">COINS</span></p>
            </div>

            <div className="border-t border-[#222530] pt-5 space-y-3">
              <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B] flex items-center gap-2"><PlusCircle className="h-3.5 w-3.5 text-[#D4AF37]" /> Send COINS</p>
              <div className="grid sm:grid-cols-[1fr_auto] gap-4 items-end">
                <div>
                  <label className={labelCls}>Amount (whole COINS)</label>
                  <input type="number" min="1" step="1" className="input-lux font-mono" value={sendAmount} onChange={(e) => setSendAmount(e.target.value)} placeholder="e.g. 200" data-testid="send-coins-amount-input" />
                </div>
                <button onClick={openConfirm} disabled={busy || !amountValid} className="btn-gold px-6 py-3 text-sm disabled:opacity-50 flex items-center gap-2 whitespace-nowrap" data-testid="send-coins-open-button">
                  <PlusCircle className="h-4 w-4" /> Send COINS
                </button>
              </div>
              <div className="flex gap-6 text-xs font-mono" data-testid="send-coins-preview">
                <span className="text-[#94A3B8]">Current: <span className="text-white">{balance.toLocaleString()}</span></span>
                <span className="text-[#94A3B8]">Balance After: <span className="text-[#D4AF37]">{balanceAfter !== null ? balanceAfter.toLocaleString() : "—"}</span></span>
              </div>
              {sendError && <p className="text-sm text-red-400" data-testid="send-coins-error">{sendError}</p>}
            </div>
          </div>

          {/* Allocation history */}
          <div className="card-lux overflow-hidden">
            <p className="px-5 pt-5 text-[10px] font-mono tracking-widest uppercase text-[#64748B]">Allocation History</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm mt-2" data-testid="allocation-history-table">
                <thead>
                  <tr className="border-b border-[#222530] text-left">
                    <th className="px-5 py-3 text-xs font-medium text-[#64748B] tracking-wide">Date</th>
                    <th className="px-5 py-3 text-xs font-medium text-[#64748B] tracking-wide">Admin</th>
                    <th className="px-5 py-3 text-xs font-medium text-[#64748B] tracking-wide text-right">Amount</th>
                    <th className="px-5 py-3 text-xs font-medium text-[#64748B] tracking-wide text-right">Before</th>
                    <th className="px-5 py-3 text-xs font-medium text-[#64748B] tracking-wide text-right">After</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#222530]">
                  {allocations.length === 0 ? (
                    <tr><td colSpan={5} className="px-5 py-8 text-center text-[#64748B]" data-testid="allocation-history-empty">No allocations yet.</td></tr>
                  ) : (
                    allocations.map((a) => (
                      <tr key={a.id} data-testid={`allocation-row-${a.id.slice(-6)}`}>
                        <td className="px-5 py-3 text-[#94A3B8] text-xs font-mono">{fmtMYT(a.created_at)}</td>
                        <td className="px-5 py-3 text-xs text-white">{a.admin_name || "—"}</td>
                        <td className="px-5 py-3 text-right text-xs font-mono text-emerald-400">+{a.amount_coins.toLocaleString()}</td>
                        <td className="px-5 py-3 text-right text-xs font-mono text-[#94A3B8]">{a.balance_before_coins.toLocaleString()}</td>
                        <td className="px-5 py-3 text-right text-xs font-mono text-[#D4AF37]">{a.balance_after_coins.toLocaleString()}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card-lux p-5 flex items-start gap-3" data-testid="ld-active-note">
            <ShieldCheck className="h-4 w-4 text-[#D4AF37] mt-0.5 shrink-0" />
            <p className="text-xs text-[#94A3B8] leading-relaxed">
              Only one active Local Depositor is allowed. COINS inventory is tracked here as an immutable ledger. Client transfers, bank/WhatsApp details, and the member Buy-via-Depositor flow arrive in later phases.
            </p>
          </div>
        </>
      )}

      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => !busy && setConfirm(null)} />
          <div className="relative card-lux w-full max-w-md p-6 sm:p-8 space-y-4" data-testid="send-confirm-modal">
            <p className="eyebrow-label">Confirm Allocation</p>
            <h3 className="font-heading text-lg font-semibold text-white">Send {confirm.amount.toLocaleString()} COINS</h3>
            <div className="space-y-1.5 text-sm">
              <p className="text-[#94A3B8]">To: <span className="text-white font-mono">{active.ib?.username}</span> <span className="text-[#64748B] font-mono text-xs">{active.ib?.uid}</span></p>
              <p className="text-[#94A3B8]">Current Balance: <span className="text-white font-mono">{balance.toLocaleString()} COINS</span></p>
              <p className="text-[#94A3B8]">Balance After: <span className="text-[#D4AF37] font-mono">{(balance + confirm.amount).toLocaleString()} COINS</span></p>
            </div>
            <div className="flex gap-3 pt-1">
              <button onClick={submitSend} disabled={busy} className="btn-gold flex-1 py-2.5 text-sm disabled:opacity-50" data-testid="send-confirm-button">
                {busy ? "Sending…" : "Confirm Send"}
              </button>
              <button onClick={() => setConfirm(null)} disabled={busy} className="btn-gold-outline px-5 py-2.5 text-sm" data-testid="send-cancel-button">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
